/**
 * @prettier
 */
import { useEffect, useState } from "react";
import { navigate } from "@reach/router";
import { auth } from "firebase-tools/firebase";
import { useUser } from "UserContext";
import { LocationType } from "types/PageType";
import { UserService } from "services/UserService";

export type AuthHookType = {
  location?: LocationType;
};

enum fallbackUrl {
  APP = "/",
  SIGNIN = "/sign-in",
  USERMANAGEMENT = "/admin-panel",
  SIGNUP = "/sign-up",
  FORGOTPASS = "/forgot-password",
}

export const useAuthHook = ({ location }: AuthHookType) => {
  const { pathname } = location as LocationType;
  const [, setUser] = useUser() as any;
  const [isAllowRender, setIsAllowRender] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(
      async (userAuth: any) => {
        if (userAuth) {
          const User = new UserService(userAuth.uid);

          // The user-doc read can hang indefinitely on a wedged Firestore
          // connection, which left the app on the loading bar forever.
          // Time it out so setIsAllowRender(true) below always runs.
          let user: any;
          let fetchFailed = false;
          let timeoutId: any;
          try {
            user = await Promise.race([
              User.getUserByDoc(),
              new Promise((_, reject) => {
                timeoutId = setTimeout(
                  () => reject(new Error("user fetch timed out")),
                  15000
                );
              }),
            ]);
          } catch (error) {
            fetchFailed = true;
          } finally {
            clearTimeout(timeoutId);
          }

          if (!fetchFailed) {
            if (!!!user?.email) {
              signOut();
            }

            if (user?.role === "user") {
              await signOut();
            }

            setUser(user);
          }
        }
        setIsAllowRender(true);
        if (
          !userAuth &&
          pathname !== fallbackUrl.SIGNUP &&
          pathname !== fallbackUrl.FORGOTPASS
        ) {
          navigate(fallbackUrl.SIGNIN);
        } else if (
          userAuth &&
          userAuth.email &&
          (pathname === fallbackUrl.SIGNIN ||
            pathname === fallbackUrl.SIGNUP ||
            pathname === fallbackUrl.APP)
        ) {
          document.body.classList.remove("body-signin");
          navigate(fallbackUrl.USERMANAGEMENT);
        }
      },
      () => {
        // auth listener error — never strand the user on the loading bar
        setIsAllowRender(true);
      }
    );
    return unsubscribe;
  }, [pathname, setUser]);

  return isAllowRender;
};

export const signOut = async () => {
  await auth.signOut();
};
